import React, { useEffect, useRef, useState } from "react";

const MAX_SECONDS = 180;

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return types.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
}

export function canRecordVoice() {
  return (
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

function formatDuration(total) {
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Records a voice message. Starts as soon as it is shown; "send" hands back an
// audio File, "cancel" throws the recording away.
export default function VoiceRecorder({ onDone, onCancel, onError, labels = {} }) {
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const sendOnStop = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const mimeType = pickMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        recorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
          if (event.data?.size) chunksRef.current.push(event.data);
        };
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          if (!sendOnStop.current) return;
          const type = recorder.mimeType || mimeType || "audio/webm";
          const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
          const blob = new Blob(chunksRef.current, { type });
          const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
          onDone(new File([blob], `message-vocal-${stamp}.${ext}`, { type }));
        };
        recorder.start();
        const started = Date.now();
        timer = setInterval(() => {
          const elapsed = (Date.now() - started) / 1000;
          setSeconds(elapsed);
          if (elapsed >= MAX_SECONDS) {
            sendOnStop.current = true;
            recorder.state !== "inactive" && recorder.stop();
            clearInterval(timer);
          }
        }, 250);
      } catch {
        if (!cancelled) onError?.();
      }
    })();
    return () => {
      cancelled = true;
      clearInterval(timer);
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") recorder.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // Runs once per recording.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = (send) => {
    sendOnStop.current = send;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
    if (!send) onCancel?.();
  };

  return (
    <div className="chat-voice" role="status">
      <button
        type="button"
        className="messages__iconBtn"
        onClick={() => stop(false)}
        aria-label={labels.cancel}
        title={labels.cancel}
      >
        <i className="fa-solid fa-trash" aria-hidden="true" />
      </button>
      <span className="chat-voice__dot" aria-hidden="true" />
      <span className="chat-voice__label">{labels.recording}</span>
      <span className="chat-voice__time" dir="ltr">
        {formatDuration(seconds)}
      </span>
      <button
        type="button"
        className="messages__send"
        onClick={() => stop(true)}
        aria-label={labels.send}
        title={labels.send}
      >
        <i className="fa-solid fa-paper-plane" aria-hidden="true" />
      </button>
    </div>
  );
}
